import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import { createPricingTimeUseCase } from "../domain/usecases/createPricingTimeUseCase";
import Topbar from "../../../components/topbar/TopBar";

const DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miercoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sabado" },
  { value: 7, label: "Domingo" },
];

function getCurrentTimeValue() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function createInitialForm() {
  const currentTime = getCurrentTimeValue();

  return {
    name: "",
    dayOfWeek: 1,
    startTime: currentTime,
    endTime: currentTime,
    multiplier: "1.0",
  };
}

function normalizeTime(value) {
  if (!value) {
    return "";
  }

  return value.length === 5 ? `${value}:00` : value;
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("created")
  );
}

export default function PricingTimeCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(createInitialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      dayOfWeek: Number(form.dayOfWeek),
      startTime: normalizeTime(form.startTime),
      endTime: normalizeTime(form.endTime),
      multiplier: Number(form.multiplier),
    };

    try {
      setSaving(true);
      const response = await createPricingTimeUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("Hay un Problema NO se logro crear su HOrario");
      }

      setMessage({
        open: true,
        type: "success",
        text: "Horario creado correctamente",
      });

      setForm(createInitialForm());
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "Hay un Problema NO se logro crear su HOrario",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      Registrar Horario
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      Registra una nueva franja horaria y su multiplicador de
                      precio.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={() => navigate("/admin/precio-horario/lista")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Ver Lista
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    mx: "auto",
                    maxWidth: 480,
                    borderRadius: 3,
                    border: "1px solid rgba(20, 83, 45, 0.12)",
                    p: { xs: 1, md: 2 },
                  }}
                >
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      select
                      label="Dia de la semana"
                      name="dayOfWeek"
                      value={form.dayOfWeek}
                      onChange={handleChange}
                    >
                      {DAYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.value} - {day.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      type="time"
                      label="Hora de inicio"
                      name="startTime"
                      value={form.startTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      type="time"
                      label="Hora de fin"
                      name="endTime"
                      value={form.endTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Multiplicador"
                      name="multiplier"
                      value={form.multiplier}
                      onChange={handleChange}
                      inputProps={{ step: "0.1", min: "1" }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#165c53",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "#12483f",
                        },
                      }}
                    >
                      {saving ? "Guardando..." : "Guardar"}
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
